/**
 * The floating assistant launcher (bottom-right, `features/agent/AgentPanel.tsx`)
 * covers whatever a view docks in the same corner. While the launcher is
 * rendered it publishes `--assistant-launcher-offset` (its height plus a gap)
 * on the document root; floating bottom-right actions add it to their own
 * bottom spacing via `aboveAssistantLauncher` and so sit above it. Without a
 * launcher the variable is unset and every offset collapses to 0.
 */
import type { Theme } from "@mui/material/styles";
import React from "react";

export const ASSISTANT_LAUNCHER_OFFSET_VAR = "--assistant-launcher-offset";

/**
 * The launcher's horizontal clearance, published next to the vertical offset:
 * its distance from the right edge plus its width plus a gap. Whatever sits at
 * the bottom right (a list's pagination) keeps this much clear on its right.
 */
export const ASSISTANT_LAUNCHER_INLINE_OFFSET_VAR =
  "--assistant-launcher-inline-offset";

/** Space kept between the launcher and content beside it. */
const LAUNCHER_INLINE_GAP_PX = 8;

/** Space kept between the launcher and an action docked above it. */
const LAUNCHER_GAP_PX = 16;

/**
 * Right margin (theme spacing units) of a page's floating action: `1` on
 * phones (below `sm`, the Page's mobile footer), `2` from there up. The
 * launcher uses the same margin so both stack in one column.
 */
export function floatActionMarginRight(isMaxSm: boolean): 1 | 2 {
  return isMaxSm ? 1 : 2;
}

/** A CSS length raised by the launcher offset (0 when there is no launcher). */
export function aboveAssistantLauncher(base: string): string {
  return `calc(${base} + var(${ASSISTANT_LAUNCHER_OFFSET_VAR}, 0px))`;
}

/**
 * Global style keeping every `TablePagination` (incl. the DataGrid footer's)
 * clear of the launcher on its right. The doubled class beats MUI's own
 * `:last-child { padding: 0 }` on the grid footer's pagination whatever the
 * style order. Without a launcher the variable is unset: no extra padding.
 * Below `md` the toolbar wraps so its content never runs under the launcher.
 */
export const launcherClearPaginationGlobalStyles = (theme: Theme) =>
  ({
    "body .MuiTablePagination-root.MuiTablePagination-root": {
      paddingRight: `var(${ASSISTANT_LAUNCHER_INLINE_OFFSET_VAR}, 0px)`,
      [theme.breakpoints.down("md")]: {
        "& .MuiTablePagination-toolbar": {
          flexWrap: "wrap",
          justifyContent: "flex-end",
        },
      },
    },
  }) as const;

/**
 * Publishes the offsets (vertical and horizontal clearance) while `active` (the
 * launcher is rendered). `rightPx` is the launcher's distance from the right
 * edge, so the horizontal clearance follows where it actually sits.
 */
export function usePublishAssistantLauncherOffset(
  active: boolean,
  launcherSizePx: number,
  rightPx: number
): void {
  React.useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    root.style.setProperty(
      ASSISTANT_LAUNCHER_OFFSET_VAR,
      `${launcherSizePx + LAUNCHER_GAP_PX}px`
    );
    root.style.setProperty(
      ASSISTANT_LAUNCHER_INLINE_OFFSET_VAR,
      `${rightPx + launcherSizePx + LAUNCHER_INLINE_GAP_PX}px`
    );
    return () => {
      root.style.removeProperty(ASSISTANT_LAUNCHER_OFFSET_VAR);
      root.style.removeProperty(ASSISTANT_LAUNCHER_INLINE_OFFSET_VAR);
    };
  }, [active, launcherSizePx, rightPx]);
}
